// Sugalarm — cycle 433: the app never presents a glucose value as current
// when it cannot PROVE the value is current.
//
// ⚠ WHY THIS FILE EXISTS. On 2026-09-13 at 23:14:50Z, during a real urgent
// low, the operator's phone sat in the foreground for 88 seconds with no
// socket and no wake handling, displaying 39 while the member's own
// receiver read 52. It ended with a force-quit. The wake event reached the
// server only at 23:23:06 — eight and a half minutes later — and that
// arrival shows no reopen, no reseed and no socket. For the whole of that
// window the number on screen was a value the app had stopped receiving,
// and nothing on the screen said so.
//
// THE PROOF OF CURRENCY IS A ROUND TRIP, NOT A TIMESTAMP. A reading's own
// timestamp says how old the SAMPLE is; it cannot say whether a newer
// sample exists that this device failed to receive. The only thing that
// can say that is the server, and the only way to hear it is a delivery:
// a reading-bearing frame (`snapshot` / `glucose`) down the socket, or a
// REST reseed that answered. So the client keeps ONE fact —
// `readingConfirmedAtMs`, the last moment the server told it what the
// newest reading is — and judges itself by the age of that fact.
//
// ⚠ THIS IS A DISPLAY RULE, NEVER AN ALARM RULE. Nothing here gates a
// takeover, a banner, a control or a sound. A member in an alarm state
// whose reading has gone unknown still shows the alarm surface and still
// sounds; only the NUMBER becomes unknown. Nothing here is read by
// `useAlarmSounds`, `useAlarmEpisode`, or any rail predicate — pinned by
// `__tests__/readingUnknown.test.ts`, which sweeps every file under
// `web/src` for it.
//
// ⚠ IT ALSO DOES NOT DOUBLE-COUNT THE SERVER'S STALENESS RULE. The server
// decides what is stale for ALARM purposes (`glucose_service`'s
// provider-aware thresholds, mirrored for DISPLAY by `lib/glucose.ts`'s
// `isStale`: Dexcom/Libre 10 min, unknown provider 15 min). That rule
// answers "is this sample old?". The bound here answers a different
// question — "is this device still being told what the newest sample is?"
// — and the two compose: a dark sensor still reads No Data at 10 minutes
// exactly as before, and a confirmed-but-old reading is judged by the
// server's rule, not by this one.

/** How long a bounded reseed may take before it is aborted and the caller's
 *  in-flight guard released. Ten seconds: long enough that a slow-but-real
 *  cellular round trip (the four reads of a wake reseed, in parallel)
 *  finishes inside it, short enough that a frozen fetch cannot hold a wake
 *  handler across a whole episode. The 2026-09-13 window was 88 s of
 *  foreground and 8.5 min end to end; ten seconds ends it at the first
 *  wake. */
export const RESEED_TIMEOUT_MS = 10_000;

/** How long the client will go without the server confirming what the
 *  newest reading is before it stops believing its own copy and goes to
 *  ask — a reopen plus a reseed.
 *
 *  Six minutes, and the reasoning: a CGM samples every 5 minutes, so in
 *  healthy operation a confirmation lands every ~5 minutes and the bound
 *  must sit above that or it would fire on the ordinary gap between two
 *  readings. It must sit BELOW the platform's 10-minute provider staleness
 *  rule, because a device that has stopped hearing from the server should
 *  discover it before the reading it is holding would be declared stale on
 *  its own age — otherwise the transport fault hides inside the sensor
 *  rule. Six minutes is the 5-minute cadence plus one minute of slack.
 *
 *  ⚠ Crossing this bound is a SUSPICION, not a verdict: it triggers a
 *  reopen and a reseed. If the reseed lands — even carrying the SAME
 *  reading — the client has proven its copy is current (the server has
 *  nothing newer) and the clock resets. A single dropped CGM sample
 *  therefore costs one reconnect and changes nothing on screen. Only a
 *  confirmation attempt that CANNOT land takes the number away. */
export const READING_CONFIRM_BOUND_MS = 6 * 60_000;

/** When the number stops being shown as current.
 *
 *  Deliberately the confirmation bound PLUS the reseed timeout, not the
 *  bound alone: at the bound we fire a reseed, and until that reseed has
 *  had its full allowance we do not yet know whether the client is out of
 *  touch or merely between readings. At `READING_CONFIRM_BOUND_MS +
 *  RESEED_TIMEOUT_MS` the attempt to confirm has demonstrably failed, and
 *  "we cannot prove this number is current" is a fact rather than a guess.
 *  Blanking at the bound alone would flash the hero to unknown for the
 *  length of every round trip. */
export const READING_UNKNOWN_AFTER_MS =
  READING_CONFIRM_BOUND_MS + RESEED_TIMEOUT_MS;

/** Is the client overdue a confirmation — i.e. should the transport reopen
 *  and reseed? Pure. `null` (nothing has ever confirmed) is overdue: a
 *  client that has never been told anything cannot be up to date.
 *
 *  ⚠ The caller supplies visibility. A hidden page is expected to be quiet
 *  and frozen, and is never judged here (the cycle-429 rule). */
export function readingConfirmationOverdue(
  confirmedAtMs: number | null,
  nowMs: number,
  boundMs: number = READING_CONFIRM_BOUND_MS,
): boolean {
  if (confirmedAtMs == null) return true;
  return nowMs - confirmedAtMs > boundMs;
}

/** May the number be presented as current? Pure, and the ONE predicate
 *  every display surface asks. `null` = nothing has ever confirmed, which
 *  is unknown by definition (there is also no reading to show in that
 *  state). A clock that has run backwards (a device time change mid-
 *  session) yields a negative age and reads as confirmed, exactly as
 *  `ageMinutes` already does — a wrong clock must not blank a live
 *  number. */
export function readingUnknown(
  confirmedAtMs: number | null,
  nowMs: number,
  afterMs: number = READING_UNKNOWN_AFTER_MS,
): boolean {
  if (confirmedAtMs == null) return true;
  return nowMs - confirmedAtMs > afterMs;
}
